import React from 'react';
import {
  HiCheckCircle,
  HiOutlineArrowDownTray,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineClipboardDocumentList,
  HiOutlineDocumentText,
  HiOutlineEyeSlash,
  HiOutlineMinusCircle,
  HiOutlinePaperAirplane,
  HiOutlinePencilSquare,
  HiOutlineUsers,
} from 'react-icons/hi2';
import { DepartmentPicker } from './DepartmentPicker';
import { PublishButton } from './PublishButton';
import { OverflowMenu } from '../ui/OverflowMenu';
import { summarizeInvites } from '../../utils/inviteActivity';

const STATUS = {
  published: { label: 'Veröffentlicht', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
  draft: { label: 'Entwurf', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
};

const MENU_ICON = 'h-4 w-4 text-slate-500 dark:text-slate-400';

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatShortDate(date) {
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** "heute, 14:31" reads faster than a full date when scanning for what just changed. */
function formatUpdated(value) {
  const date = parseDate(value);
  if (!date) return null;

  const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const daysAgo = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);

  if (daysAgo === 0) return `heute, ${time}`;
  if (daysAgo === 1) return `gestern, ${time}`;
  return `${formatShortDate(date)}, ${time}`;
}

function openInNewTab(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** One line of the "what is attached" checklist: a real state, not a button. */
function ReadinessItem({ done, children }) {
  const Icon = done ? HiCheckCircle : HiOutlineMinusCircle;
  return (
    <li className={`inline-flex items-center gap-1 ${done ? 'text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
      <Icon
        className={`h-4 w-4 shrink-0 ${done ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
        aria-hidden="true"
      />
      {children}
      <span className="sr-only">{done ? 'vorhanden' : 'fehlt'}</span>
    </li>
  );
}

/** "2 in Arbeit · 1 inaktiv · 1 abgeschickt", or how many are invited when nobody started yet. */
function InviteSummaryLine({ invites }) {
  const summary = summarizeInvites(invites);
  if (summary.total === 0) return null;

  const parts = [
    summary.inWork > 0 && { text: `${summary.inWork} in Arbeit` },
    summary.inactive > 0 && { text: `${summary.inactive} inaktiv`, tone: 'font-medium text-amber-700 dark:text-amber-400' },
    summary.submitted > 0 && { text: `${summary.submitted} abgeschickt` },
  ].filter(Boolean);
  if (parts.length === 0) parts.push({ text: `${summary.total} eingeladen` });

  return (
    <p className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
      <HiOutlineUsers className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" aria-hidden="true" />
      <span>
        {parts.map((part, index) => (
          <React.Fragment key={part.text}>
            {index > 0 && <span className="text-slate-300 dark:text-slate-600"> · </span>}
            <span className={part.tone}>{part.text}</span>
          </React.Fragment>
        ))}
      </span>
    </p>
  );
}

/**
 * One job listing, read top to bottom: status, title, what is attached, then one
 * action, with the dates as a footnote. Everything else a job can do sits in the
 * "…" menu, so 27 cards in a grid show 27 clear next steps instead of 27 rows of
 * equal buttons.
 *
 * "Ausgeschrieben am" (datePosted) is deliberately absent: it is the Google Jobs
 * date from the import, identical across a handover, and next to the status it
 * read as the day the job went live.
 */
export function JobCard({ job, departments, canEdit, hasAssessment, channels, invites, onOpenAssessment, onOpenChannelTexts, onOpenFiles, onDeactivate, onJobChanged, onDepartmentCreated }) {
  const status = STATUS[job.status] || STATUS.draft;
  const validThrough = parseDate(job.validThrough);
  const updated = formatUpdated(job.updatedAt);
  const hasChannelTexts = Boolean(channels?.indeed || channels?.afa);

  const menuItems = [
    { label: 'Im CMS bearbeiten', icon: <HiOutlinePencilSquare className={MENU_ICON} />, onClick: () => openInNewTab(job.adminUrl) },
    {
      // Der Knopf daneben führt in den Einladungs-Reiter desselben Dialogs. Das
      // Dokument selbst lag damit eine Ebene tiefer als alles andere, was man an
      // einer Stelle hinterlegt — und wurde genau deshalb nicht gefunden.
      label: hasAssessment ? 'Fragebogen 2 bearbeiten' : 'Fragebogen 2 einrichten',
      icon: <HiOutlineClipboardDocumentList className={MENU_ICON} />,
      onClick: () => onOpenAssessment?.(job, 'setup'),
    },
    {
      label: hasChannelTexts ? 'Indeed & AfA bearbeiten' : 'Indeed & AfA hinterlegen',
      icon: <HiOutlineDocumentText className={MENU_ICON} />,
      onClick: () => onOpenChannelTexts?.(job),
    },
    { label: 'Dateien herunterladen', icon: <HiOutlineArrowDownTray className={MENU_ICON} />, onClick: () => onOpenFiles?.(job) },
    {
      label: job.campaignUrl ? 'Kampagnenseite öffnen' : 'Keine Kampagnenseite',
      icon: <HiOutlineArrowTopRightOnSquare className={MENU_ICON} />,
      onClick: () => openInNewTab(job.campaignUrl),
      disabled: !job.campaignUrl,
      separatorBefore: true,
    },
    job.indeedUrl && {
      label: 'Indeed-Anzeige öffnen',
      icon: <HiOutlineArrowTopRightOnSquare className={MENU_ICON} />,
      onClick: () => openInNewTab(job.indeedUrl),
    },
    canEdit && job.status === 'published' && {
      label: 'Deaktivieren',
      icon: <HiOutlineEyeSlash className="h-4 w-4" />,
      onClick: () => onDeactivate?.(job),
      danger: true,
      separatorBefore: true,
    },
  ];

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 p-5 shadow-xs transition-colors hover:border-slate-300 dark:hover:border-slate-600">
      <div className="flex min-h-6 flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${status.text}`}>
          <span className={`h-2 w-2 rounded-full ${status.dot}`} aria-hidden="true" />
          {status.label}
        </span>
        <PublishButton job={job} canEdit={canEdit} onPublished={onJobChanged} />
      </div>

      <h3 className="mt-2 text-lg font-semibold leading-snug text-slate-900 dark:text-slate-50">{job.title}</h3>
      {job.role && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{job.role}</p>}

      <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
        <DepartmentPicker
          job={job}
          departments={departments}
          canEdit={canEdit}
          onSaved={onJobChanged}
          onDepartmentCreated={onDepartmentCreated}
        />
      </div>

      {/* mb-5 keeps the gap to the divider when mt-auto below collapses to 0. */}
      <div className="mt-4 mb-5 space-y-2">
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label="Unterlagen">
          <ReadinessItem done={hasAssessment}>Fragebogen 2</ReadinessItem>
          <ReadinessItem done={channels?.indeed}>Indeed</ReadinessItem>
          <ReadinessItem done={channels?.afa}>AfA</ReadinessItem>
        </ul>
        <InviteSummaryLine invites={invites} />
      </div>

      {/* mt-auto pins the footer to the card's bottom edge, so the actions line
          up across a row no matter how many lines a title wraps to. */}
      <div className="mt-auto border-t border-slate-100 dark:border-slate-700/60 pt-4">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpenAssessment?.(job, hasAssessment ? 'invites' : 'setup')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-xs transition hover:bg-blue-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800"
          >
            <HiOutlinePaperAirplane className="h-4 w-4" aria-hidden="true" />
            {hasAssessment ? 'Einladung senden' : 'Fragebogen 2 einrichten'}
          </button>
          <OverflowMenu items={menuItems} variant="outline" label="Weitere Aktionen" />
        </div>
        {(updated || validThrough) && (
          <p className="mt-3 flex flex-wrap gap-x-2 text-xs text-slate-500 dark:text-slate-400">
            {updated && <span>Aktualisiert {updated}</span>}
            {updated && validThrough && <span aria-hidden="true">·</span>}
            {validThrough && <span>gültig bis {formatShortDate(validThrough)}</span>}
          </p>
        )}
      </div>
    </article>
  );
}
